import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Flex, Spinner } from 'components';
import { P } from 'components/Typography/Typography';
import { Button, TextField } from 'components/_form';
import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { ILogin } from 'types/forms/auth';
import { loginSchema } from 'validation/authSchemas';
import { login } from '../api/auth';
import { meKeys } from 'api/queryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { SESSION_KEY } from 'hooks/queries/useMeQuery';

const Wrapper = styled(Flex)`
  max-width: 400px;
  width: auto;
  min-width: 230px;
  margin: 0 auto;
`;

const TenantToggle = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme?.colors?.grey || '#666'};
  font-size: 0.8rem;
  text-decoration: underline;
  margin-top: -8px;
  margin-bottom: 4px;
  display: inline-block;
`;

export const LoginView: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);

  // Jeśli URL zawiera ?company=<id_code> — pole jest zablokowane i widoczne od razu.
  const prefilledTenantCode = searchParams.get('company') || '';
  const [showTenantField, setShowTenantField] = useState(!!prefilledTenantCode);
  const [tenantCode, setTenantCode] = useState(prefilledTenantCode);

  const {
    register,
    handleSubmit,
    formState,
    formState: { errors }
  } = useForm<ILogin>({
    mode: 'onChange',
    defaultValues: {
      user: {
        email: '',
        password: ''
      }
    },
    resolver: yupResolver(loginSchema)
  });

  const onSubmit = handleSubmit(async (data) => {
    setIsLoading(true);

    try {
      const response = await login({ ...data, tenantCode: tenantCode || undefined });
      if (!response) return;
      localStorage.setItem('currentUser', JSON.stringify(response.data));
      localStorage.setItem(SESSION_KEY, '1');
      localStorage.removeItem('current-company-id');
      await queryClient.invalidateQueries({ queryKey: meKeys.all() });
      navigate('/app/desktop');
    } catch (err: any) {
      toast.error(err?.message || t('anErrorOccurred'));
    } finally {
      setIsLoading(false);
    }
  });

  useEffect(() => {
    if (localStorage.getItem(SESSION_KEY)) {
      navigate('/app/desktop');
    }
  }, [navigate]);

  return (
    <Wrapper justifyContent="center" flexWrap="wrap" flexDirection="column">
      {isLoading && <Spinner />}
      <form onSubmit={onSubmit}>
        {showTenantField && (
          <Box mb={2}>
            <TextField
              label={t('auth.tenantCode')}
              transparent
              value={tenantCode}
              onChange={(e) => setTenantCode(e.target.value)}
              disabled={!!prefilledTenantCode}
            />
          </Box>
        )}

        <TextField
          label="common.username"
          transparent
          {...register('user.email')}
          error={t(errors?.user?.email?.message || '')}
        />
        <Box mt={2} mb={2}>
          <TextField
            type="password"
            label={t('auth.password')}
            transparent
            {...register('user.password')}
            error={t(errors?.user?.password?.message || '')}
          />
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Link to="/password-reset">
            <P variant="body2" color="grey">
              {t('auth.forgotPass')}
            </P>
          </Link>
          {!prefilledTenantCode && (
            <TenantToggle
              type="button"
              onClick={() => {
                setShowTenantField((v) => !v);
                if (showTenantField) setTenantCode('');
              }}
            >
              {showTenantField ? t('auth.hideTenantCode') : t('auth.loginWithCompany')}
            </TenantToggle>
          )}
        </Box>

        <Box my={4}>
          <Button fullWidth type="submit" disabled={!formState.isValid || isLoading}>
            {t('auth.login')}
          </Button>
        </Box>
      </form>
    </Wrapper>
  );
};
